import {
  Button,
  Divider,
  Empty,
  Modal,
  Space,
  Typography,
  Upload,
  message,
} from "antd";
import React from "react";
import { useMediaQuery } from "@mui/material";
import { _ObjectType, FilesReturnType, S3FileWithUrl } from "~/types";
import { getFileUrl, INTENTS } from "~/utils";
import {
  buildFileModalCloseSearch,
  buildFileModalDocsSearch,
  isFileModalOpenForEntity,
} from "~/utils/fileModalUrl";
import { useTranslation } from "react-i18next";
import {
  Outlet,
  useFetcher,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
} from "react-router-dom";
import { Loading3QuartersOutlined, PlusOutlined } from "@ant-design/icons";
import { CustomTextField } from "../TextField";
import { S3ServiceLambda } from "~/services/s3";

export const FileModal = ({
  entityID,
  entityType,
  baseURL,
  disabled,
  disableActions,
}: {
  entityID: string;
  entityType: _ObjectType;
  baseURL: string;
  disabled?: boolean;
  disableActions?: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const isOpen = isFileModalOpenForEntity(
    location.pathname,
    location.search,
    baseURL,
    entityID,
    entityType,
  );

  const openDocs = () => {
    navigate({
      pathname: `${baseURL}/docs`,
      search: buildFileModalDocsSearch(
        location.search,
        entityType,
        entityID,
        disableActions,
      ),
    });
  };

  const closeDocs = () => {
    const search = buildFileModalCloseSearch(location.search);
    navigate({
      pathname: baseURL,
      search,
    });
  };

  return (
    <>
      <Space direction="vertical" key={"image-modal-space"}>
        <Button onClick={openDocs} size="small" disabled={disabled}>
          {t("View_More")}
        </Button>
      </Space>

      <Modal
        open={isOpen}
        footer={[]}
        onCancel={closeDocs}
        width={isMobile ? "calc(100vw - 32px)" : 720}
        style={
          isMobile
            ? { top: 16, maxWidth: "100vw", paddingBottom: 0 }
            : { minWidth: 400 }
        }
        destroyOnClose={false}
      >
        {isOpen ? <Outlet /> : null}
      </Modal>
    </>
  );
};

export type FileModalContentLoaderType = {
  files: FilesReturnType;
  objectType: _ObjectType;
  entityID: string;
  disableActions?: boolean;
};

export const FileModalContent = () => {
  const { t } = useTranslation();
  const loaderData = useLoaderData() as FileModalContentLoaderType;
  const fileList = loaderData.files;
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [loading, setLoading] = React.useState(false);
  const [allowUpload, setAllowUpload] = React.useState(false);

  const [uploadingFile, setUploadingFile] = React.useState(false);

  const [currentFile, setCurrentFile] = React.useState<S3FileWithUrl | null>();
  const [currentImage, setCurrentImage] = React.useState<S3FileWithUrl | null>();
  const [newFile, setNewFile] = React.useState<File | null>(null);
  const fetcher = useFetcher();

  const navigation = useNavigation();

  const isSubmitting =
    navigation.state === "submitting" || fetcher.state !== "idle";
  const isLoading = loading || isSubmitting;

  const shouldDisable = isLoading;

  const beforeUpload = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error("File must be smaller than 2MB!");
    }

    setAllowUpload(isLt2M);
    return isLt2M;
  };

  const handleFileChange = (info: any) => {
    if (info.fileList.length > 0) {
      setLoading(true);

      const file: File = info.fileList[0].originFileObj;
      setNewFile(file);
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!newFile) {
      message.error(t("No_file_selected"));
      return;
    }

    const result = await S3ServiceLambda.upload({
      entityID: loaderData.entityID,
      entityType: loaderData.objectType,
      file: newFile,
      name: newFile?.name || "",
    });

    if (result.success) {
      message.success(t("File_successfully_uploaded"));
    } else {
      message.error(t("File_upload_failed"));
    }

    setCurrentFile(null);
    setUploadingFile(false);
    setNewFile(null);

    fetcher.submit(
      { body: "Empty value to trigger action" },
      { method: "post" },
    );
  };

  const handleDeleteFile = () => {
    fetcher.submit(
      {
        body: currentFile?.Key || currentImage?.Key || "",
        intent: INTENTS._delete_file,
      },
      { method: "post" },
    );
    setCurrentFile(null);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? 12 : 0,
        maxWidth: "100%",
      }}
    >
      <Space direction="vertical">
        <Typography.Text strong>
          {t("Files")} {isLoading && <Loading3QuartersOutlined spin />}
        </Typography.Text>
        {fileList.files?.map((file) => (
          <Button
            onClick={() => {
              setUploadingFile(false);
              setCurrentFile(file);
              setCurrentImage(null);
            }}
            size="small"
            type="text"
            key={file.Key || ""}
            disabled={shouldDisable}
          >
            {(file.Key || "").split("/").pop() || ""}
            {currentFile?.Key === file.Key && " >"}
          </Button>
        ))}

        <Divider type="horizontal" />
        <Typography.Text strong>{t("Images")}</Typography.Text>
        {fileList.images?.map((image) => (
          <Button
            onClick={() => {
              setUploadingFile(false);
              setCurrentImage(image);
              setCurrentFile(null);
            }}
            size="small"
            type="text"
            key={image.Key || ""}
            disabled={shouldDisable}
          >
            {(image.Key || "").split("/").pop() || ""}
            {currentImage?.Key === image.Key && " >"}
          </Button>
        ))}

        <Button
          onClick={() => {
            setUploadingFile(true);
            setCurrentFile(null);
            setCurrentImage(null);
          }}
          size="small"
          type="text"
          disabled={shouldDisable || loaderData.disableActions}
        >
          {t("Upload_file")}
          {uploadingFile && " >"}
        </Button>
      </Space>

      <Divider type={isMobile ? "horizontal" : "vertical"} />

      {uploadingFile ? (
        <Space direction="vertical">
          <Typography.Text strong>{t("Details")}</Typography.Text>
          <Upload
            listType="picture-card"
            maxCount={1}
            beforeUpload={beforeUpload}
            onPreview={() => {}}
            onChange={handleFileChange}
          >
            <button style={{ border: 0, background: "none" }} type="button">
              {loading ? <Loading3QuartersOutlined spin /> : <PlusOutlined />}
              <div style={{ marginTop: 8 }}>{t("Upload_file")}</div>
            </button>
          </Upload>
          <Button
            onClick={() => handleFileUpload()}
            type="primary"
            size="small"
            disabled={!newFile || !allowUpload}
          >
            {t("Submit")}
          </Button>
        </Space>
      ) : (
        <Space direction="vertical">
          <Typography.Text strong>{t("Details")}</Typography.Text>
          <div style={{ width: "100%", marginTop: "2rem" }}>
            {currentFile ? (
              <CustomTextField
                value={t("View_More")}
                link={getFileUrl(currentFile)}
                newPage={true}
              />
            ) : currentImage ? (
              <img
                src={getFileUrl(currentImage)}
                alt=""
                style={{
                  maxWidth: "100%",
                  width: isMobile ? "100%" : 200,
                  height: "auto",
                  maxHeight: 200,
                  objectFit: "contain",
                }}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </div>
          <Button
            onClick={() => handleDeleteFile()}
            type="primary"
            danger
            size="small"
            disabled={
              (!currentFile && !currentImage) || loaderData.disableActions
            }
          >
            {t("Delete")}
          </Button>
        </Space>
      )}
    </div>
  );
};
